import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import WelcomeHeader from './components/WelcomeHeader';
import UserTypeCard from './components/UserTypeCard';
import ActionButtons from './components/ActionButtons';
import FirstCheckInBanner from '../../components/FirstCheckInBanner';
import { Users, Activity, Accessibility, Zap, Heart, Bell } from 'lucide-react';

// Profile-specific feature highlights config
const PROFILE_HIGHLIGHTS = {
  child: {
    label: 'Caregiver',
    color: 'from-blue-50 to-indigo-50 border-blue-200',
    iconColor: 'text-blue-600',
    badgeColor: 'bg-blue-100 text-blue-700',
    icon: Users,
    title: 'Caregiver Features Available',
    description: 'You have access to tools designed specifically for managing dependents\' health.',
    highlights: [
      { icon: Users, text: 'Add & manage dependent profiles (children, elderly parents, family members)' },
      { icon: Bell, text: 'Receive caregiver alerts when dependents log critical symptoms' },
      { icon: Activity, text: 'View side-by-side analytics across all your dependents' },
    ],
    cta: { label: 'Manage Dependents', path: '/user-relationship-management' },
  },
  chronic: {
    label: 'Long Term Condition',
    color: 'from-orange-50 to-amber-50 border-orange-200',
    iconColor: 'text-orange-600',
    badgeColor: 'bg-orange-100 text-orange-700',
    icon: Activity,
    title: 'Trigger Tracking Highlights',
    description: 'Track what triggers your symptoms and spot patterns over time.',
    highlights: [
      { icon: Zap, text: 'Log specific triggers (food, stress, weather, activity) with every check-in' },
      { icon: Activity, text: 'Advanced trigger correlation analysis in your symptom analytics' },
      { icon: Heart, text: 'Distinguish flare-ups from new symptoms with long-term trend charts' },
    ],
    cta: { label: 'View Symptom Analytics', path: '/advanced-symptom-analytics' },
  },
  elderly: {
    label: 'Elderly Person',
    color: 'from-green-50 to-teal-50 border-green-200',
    iconColor: 'text-green-600',
    badgeColor: 'bg-green-100 text-green-700',
    icon: Accessibility,
    title: 'Accessibility Features',
    description: 'MyBodyToday is designed to be easy to use for older adults.',
    highlights: [
      { icon: Accessibility, text: 'Large touch targets and high-contrast interface throughout the app' },
      { icon: Bell, text: 'Daily check-in reminders to help maintain a consistent health log' },
      { icon: Heart, text: 'Simplified check-in questions focused on balance, falls, and daily activities' },
    ],
    cta: { label: 'Start Daily Check-in', path: '/daily-check-in' },
  },
};

const ProfileHighlightBanner = ({ userType, onNavigate }) => {
  const config = PROFILE_HIGHLIGHTS?.[userType];
  if (!config) return null;

  const IconComponent = config?.icon;

  return (
    <div className={`mt-6 rounded-xl border bg-gradient-to-br ${config?.color} p-5`}>
      <div className="flex items-start gap-3 mb-4">
        <div className={`flex-shrink-0 w-9 h-9 rounded-full bg-white/70 flex items-center justify-center shadow-sm`}>
          <IconComponent className={`w-5 h-5 ${config?.iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-foreground">{config?.title}</h3>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config?.badgeColor}`}>
              {config?.label}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">{config?.description}</p>
        </div>
      </div>
      <ul className="space-y-2 mb-4">
        {config?.highlights?.map((item, idx) => {
          const ItemIcon = item?.icon;
          return (
            <li key={idx} className="flex items-start gap-2.5">
              <ItemIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${config?.iconColor}`} />
              <span className="text-sm text-foreground">{item?.text}</span>
            </li>
          );
        })}
      </ul>
      <button
        onClick={() => onNavigate(config?.cta?.path)}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-white/80 hover:bg-white transition-colors shadow-sm border border-white/60 ${config?.iconColor}`}
      >
        {config?.cta?.label}
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
};

const UserTypeSelection = () => {
  const navigate = useNavigate();
  const { updateProfile, userProfile } = useAuth();
  const [selectedUserType, setSelectedUserType] = useState('');
  const [saving, setSaving] = useState(false);

  const userTypes = [
    {
      id: 'general',
      label: 'General Adult',
      description: 'Tracking occasional health symptoms. Standard questions about symptoms, severity, and daily impact.',
      icon: 'User'
    },
    {
      id: 'child',
      label: 'Caregiver',
      description: 'Tracking symptoms for a dependent (child, elderly parent, or family member). Questions will focus on behavior, eating, energy levels, and daily activities.',
      icon: 'Baby'
    },
    {
      id: 'chronic',
      label: 'Someone with Long Term Condition',
      description: 'Living with an ongoing health condition. Questions will help distinguish between normal flare-ups and new symptoms.',
      icon: 'Activity'
    },
    {
      id: 'elderly',
      label: 'Elderly Person',
      description: 'Managing health for an older adult. Questions will include confusion, balance, falls, and daily living activities.',
      icon: 'Users'
    }
  ];

  useEffect(() => {
    const savedUserType = localStorage.getItem('userType');
    if (savedUserType) {
      setSelectedUserType(savedUserType);
    }
  }, [navigate]);

  // Resolve effective user type: prefer DB profile, fall back to localStorage
  const effectiveUserType = userProfile?.user_type || selectedUserType;
  // Map DB value 'caregiver' back to 'child' for highlight lookup
  const highlightKey = effectiveUserType === 'caregiver' ? 'child' : effectiveUserType;

  const handleUserTypeSelect = (typeId) => {
    setSelectedUserType(typeId);
  };

  const handleContinue = async () => {
    if (selectedUserType && !saving) {
      setSaving(true);
      try {
        // Save to localStorage
        localStorage.setItem('userType', selectedUserType);
        localStorage.setItem('hasCompletedOnboarding', 'true');
        
        // Save to database - this will map 'child' -> 'caregiver' in AuthContext
        const result = await updateProfile({ 
          user_type: selectedUserType,
          onboarding_completed: true 
        });
        
        if (result?.error) {
          console.error('Failed to save user type to database:', result?.error);
          // Still navigate even if database update fails (localStorage is backup)
        }
        
        navigate('/profile-selection-success', { state: { userType: selectedUserType } });
      } catch (error) {
        console.error('Error saving user type:', error);
        // Still navigate to avoid blocking user
        navigate('/profile-selection-success', { state: { userType: selectedUserType } });
      } finally {
        setSaving(false);
      }
    }
  };

  const handleSkip = async () => {
    if (!saving) {
      setSaving(true);
      try {
        localStorage.setItem('userType', 'general');
        localStorage.setItem('hasCompletedOnboarding', 'true');
        
        // Save to database
        await updateProfile({ 
          user_type: 'general',
          onboarding_completed: true 
        });
        
        navigate('/profile-selection-success', { state: { userType: 'general' } });
      } catch (error) {
        console.error('Error saving user type:', error);
        navigate('/profile-selection-success', { state: { userType: 'general' } });
      } finally {
        setSaving(false);
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>User Type Selection - MyBodyToday</title>
        <meta 
          name="description" 
          content="Select your user type to personalize your symptom tracking experience with MyBodyToday" 
        />
      </Helmet>
      <div className="min-h-screen bg-background flex items-center justify-center p-4 md:p-6 lg:p-8">
        {/* First Check-In Banner - Shows after email verification */}
        <FirstCheckInBanner />
        
        <div className="w-full max-w-6xl mx-auto py-4 md:py-8 lg:py-12">
          <WelcomeHeader />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8 mb-6 md:mb-8 lg:mb-10">
            {userTypes?.map((type) => (
              <UserTypeCard
                key={type?.id}
                id={type?.id}
                label={type?.label}
                description={type?.description}
                icon={type?.icon}
                isSelected={selectedUserType === type?.id}
                onSelect={handleUserTypeSelect}
              />
            ))}
          </div>

          {/* Profile-specific feature highlights */}
          <ProfileHighlightBanner
            userType={highlightKey}
            onNavigate={navigate}
          />

          <ActionButtons
            selectedUserType={selectedUserType}
            onContinue={handleContinue}
            onSkip={handleSkip}
            disabled={saving}
          />
        </div>
      </div>
    </>
  );
};

export default UserTypeSelection;